/**
 * User Management Service
 *
 * Handles all user management operations including user creation, updates, deletion,
 * status management, and bulk operations. Provides comprehensive user management
 * functionality for coordinators and administrators.
 *
 * @example
 * ```typescript
 * import { UserManagementService } from './userManagementService';
 *
 * // Create a new user
 * const response = await UserService.createUser(userData);
 *
 * // Get user management statistics
 * const stats = await UserManagementService.getUserManagementStats();
 *
 * // Search users
 * const users = await UserManagementService.searchUsers('john@example.com');
 * ```
 */
import { apiClient, handleApiError, isNetworkError, isAuthError } from '../utils/apiClient';
import { isDevBackendUnreachableClientError } from '../utils/backendUnavailable';
import { User, UserRole, UserStatus, ApiResponse, } from '../../shared/types';
import type { UserGeoLocationInput, UserGeoLocationSource } from '../../shared/types/userGeo';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
// Temporary ApiError class (align arity with call sites: message, status, code, details)
class ApiError extends Error {
    public readonly status: number;
    public readonly code: string;
    public readonly details?: unknown;

    constructor(message: string, status: number = 500, code: string = 'API_ERROR', details?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}
import { getAuthToken } from '../utils/localStorageCache';
// API Configuration
const API_BASE_URL = '/api';
const DEFAULT_CACHE_DURATION = 30000; // 30 seconds
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
// Type Definitions
export interface CreateUserRequest {
    first_name: string;
    last_name: string;
    email: string;
    cell_phone: string;
    state_commission_id?: string;
    user_state: string;
    user_county: string;
    user_city: string;
    user_zip_code: string;
    agency_id: number;
    created_by: number;
    location?: UserGeoLocationInput;
    locationSource?: UserGeoLocationSource;
}
export interface CreateUserResponse {
    success: boolean;
    message: string;
    data?: {
        user_id: number;
        email: string;
        password_setup_sent: boolean;
    };
    error?: string;
}
export interface UserManagementStats {
    total_users: number;
    active_users: number;
    pending_activations: number;
    recent_creations: number;
}
export interface UserListItem {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    role: UserRole;
    status: UserStatus;
    created_at: string;
    last_login?: string;
    profile_image?: string;
    phone?: string;
    office_name?: string;
    office_phone?: string;
}
export interface UserSearchFilters {
    page?: number;
    limit?: number;
    search?: string;
    role?: UserRole;
    status?: UserStatus;
    agency_id?: number;
    created_after?: string; // ISO date format
    created_before?: string; // ISO date format
}
export interface BulkCreateUserResult {
    email: string;
    success: boolean;
    user_id?: number;
    error?: string;
}
export interface BulkCreateUsersResponse {
    success: boolean;
    message: string;
    results: BulkCreateUserResult[];
    summary: {
        total: number;
        successful: number;
        failed: number;
    };
}
export interface UserStatusUpdateRequest {
    status: UserStatus;
    reason?: string;
}
export interface UserStatusUpdateResponse {
    success: boolean;
    message: string;
    data?: {
        user_id: number;
        previous_status: UserStatus;
        new_status: UserStatus;
        updated_at: string;
    };
}
/**
 * User Management Service Class
 *
 * Provides comprehensive user management functionality with caching, error handling,
 * retry logic, and proper authentication integration.
 */
class UserManagementService {
    private requestCache = new Map<string, {
        data: any;
        timestamp: number;
        expiresAt: number;
    }>();
    private readonly maxCacheSize = 100;
    private pendingRequests = new Map<string, Promise<any>>();
    /**
     * Generate cache key for request
     */
    private generateCacheKey(endpoint: string, options: RequestInit = {}): string {
        const method = options.method || 'GET';
        const body = options.body ? JSON.stringify(options.body) : '';
        const headers = options.headers ? JSON.stringify(options.headers) : '';
        return `${method}:${endpoint}:${body}:${headers}`;
    }
    /**
     * Get cached response if available and not expired
     */
    private getCachedResponse(cacheKey: string): any | null {
        const cached = this.requestCache.get(cacheKey);
        if (cached && Date.now() < cached.expiresAt) {
            // Cache hit for user management
            return cached.data;
        }
        if (cached) {
            this.requestCache.delete(cacheKey);
        }
        return null;
    }
    /**
     * Store response in cache
     */
    private setCachedResponse(cacheKey: string, data: any, duration: number = DEFAULT_CACHE_DURATION): void {
        this.cleanupCache();
        if (this.requestCache.size >= this.maxCacheSize) {
            const oldestKey = this.requestCache.keys().next().value;
            this.requestCache.delete(oldestKey);
        }
        this.requestCache.set(cacheKey, {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + duration
        });
        // Cached user management response
    }
    /**
     * Clean up expired cache entries
     */
    private cleanupCache(): void {
        const now = Date.now();
        for (const [key, value] of this.requestCache.entries()) {
            if (now >= value.expiresAt) {
                this.requestCache.delete(key);
            }
        }
    }
    /**
     * Sleep utility for retry delays
     */
    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * Execute request with retry logic and caching
     */
    private async executeRequest<T>(endpoint: string, options: RequestInit = {}, cacheEnabled: boolean = true, cacheDuration: number = DEFAULT_CACHE_DURATION): Promise<T> {
        const cacheKey = this.generateCacheKey(endpoint, options);
        const method = options.method || 'GET';
        // Check for pending identical requests to prevent duplicates
        if (this.pendingRequests.has(cacheKey)) {
            // Waiting for pending identical user management request
            return this.pendingRequests.get(cacheKey)!;
        }
        // Check cache for GET requests
        if (cacheEnabled && method === 'GET') {
            const cachedResponse = this.getCachedResponse(cacheKey);
            if (cachedResponse !== null) {
                return cachedResponse;
            }
        }
        // Create the request promise
        const requestPromise = this.makeRequestWithRetry<T>(endpoint, options, cacheKey, cacheEnabled, cacheDuration);
        // Store pending request
        this.pendingRequests.set(cacheKey, requestPromise);
        try {
            const result = await requestPromise;
            return result;
        }
        finally {
            // Clean up pending request
            this.pendingRequests.delete(cacheKey);
        }
    }
    /**
     * Make request with retry logic
     */
    private async makeRequestWithRetry<T>(endpoint: string, options: RequestInit, cacheKey: string, cacheEnabled: boolean, cacheDuration: number): Promise<T> {
        let lastError: Error | null = null;
        for (let attempt = 0; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
            try {
                const response = await this.makeHttpRequest(endpoint, options);
                const data = await this.processResponse<T>(response);
                // Cache successful GET responses
                if (cacheEnabled && response.ok && (options.method === 'GET' || !options.method)) {
                    this.setCachedResponse(cacheKey, data, cacheDuration);
                }
                return data;
            }
            catch (error) {
                lastError = error as Error;
                if (isDevBackendUnreachableClientError(error)) {
                    throw lastError;
                }
                // Don't retry on certain errors
                if (error instanceof ApiError &&
                    (error.status === 401 || error.status === 403 || error.status === 404 || error.status === 422)) {
                    throw error;
                }
                // Don't retry on the last attempt
                if (attempt === MAX_RETRY_ATTEMPTS) {
                    break;
                }
                // User management request failed, retrying
                await this.sleep(RETRY_DELAY_MS * Math.pow(2, attempt)); // Exponential backoff
            }
        }
        throw lastError || new Error('User management request failed after all retry attempts');
    }
    /**
     * Make the actual HTTP request
     */
    private async makeHttpRequest(endpoint: string, options: RequestInit): Promise<Response> {
        const url = `${API_BASE_URL}${endpoint}`;
        const token = getAuthToken();
        const defaultOptions: RequestInit = {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...options.headers,
            },
            ...options,
        };
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        try {
            // Making user management request
            const response = await fetch(url, {
                ...defaultOptions,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            // User management response received
            return response;
        }
        catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }
    /**
     * Process the HTTP response and extract data
     */
    private async processResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            logger.api('❌ User management HTTP error:', response.status, response.statusText);
            let errorData: any = undefined;
            try {
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    errorData = await response.json();
                }
            }
            catch (parseError) {
                logger.api('Could not parse user management error response:', parseError);
            }
            // Handle specific error cases
            if (response.status === 401) {
                throw new ApiError('Unauthorized access to user management', 401, 'UNAUTHORIZED');
            }
            if (response.status === 403) {
                throw new ApiError('Access forbidden for user management', 403, 'FORBIDDEN');
            }
            if (response.status === 404) {
                throw new ApiError('User management resource not found', 404, 'NOT_FOUND');
            }
            if (response.status === 422) {
                const validationErrors = errorData?.errors || errorData?.validation_errors || [];
                throw new ApiError('User management validation failed', 422, 'VALIDATION_ERROR', validationErrors);
            }
            if (response.status === 429) {
                throw new ApiError('Rate limit exceeded for user management', 429, 'RATE_LIMIT');
            }
            if (response.status >= 500) {
                throw new ApiError('Internal server error in user management', response.status, 'SERVER_ERROR');
            }
            const errorMessage = (errorData?.error || errorData?.message || errorData?.details) ??
                `HTTP ${response.status}: ${response.statusText}`;
            throw new ApiError(errorMessage, response.status, 'API_ERROR');
        }
        // Check if response is empty
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0') {
            logger.api('⚠️ Empty user management response received', "GET");
            return { success: false, error: 'Empty response from server' } as T;
        }
        // Handle JSON response
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            try {
                const data = await response.json();
                // User management JSON response data received
                return data as T;
            }
            catch (jsonError) {
                logger.api('❌ Failed to parse user management JSON response:', jsonError);
                const text = await response.text();
                logger.api('📄 Raw user management response text:', text);
                throw new ApiError('Invalid JSON response from user management server', 500, 'INVALID_JSON');
            }
        }
        // Default to text if no specific type is requested
        const text = await response.text();
        // User management text response received (default)
        return text as T;
    }
    /**
     * Create a new user account (for coordinators only)
     * @param userData - User creation data
     * @returns Promise resolving to user creation response
     */
    async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
        try {
            // Validate required fields
            if (!userData.email || !userData.first_name || !userData.last_name) {
                throw new Error('Email, first name, and last name are required');
            }
            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(userData.email)) {
                throw new Error('Invalid email format');
            }
            const response = await this.executeRequest<CreateUserResponse>('/users/create', {
                method: 'POST',
                body: JSON.stringify(userData),
            }, false); // Don't cache POST requests
            // Clear relevant caches after successful creation
            this.clearCacheForPattern('/users/management/stats');
            this.clearCacheForPattern('/users/agency');
            this.clearCacheForPattern('/users/recent');
            return response;
        }
        catch (error) {
            logger.error('Error creating user:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to create user account', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Get user management statistics
     * @returns Promise resolving to user management statistics
     */
    async getUserManagementStats(): Promise<UserManagementStats> {
        try {
            const response = await this.executeRequest<ApiResponse<UserManagementStats>>('/users/management/stats', {
                method: 'GET',
            }, true, 60000); // Cache for 1 minute
            return response.data || {} as UserManagementStats;
        }
        catch (error) {
            logger.error('Error fetching user management stats:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Failed to fetch user management statistics', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Get list of users for the current user's agency
     * @param filters - Optional filters for the user list
     * @returns Promise resolving to list of agency users
     */
    async getAgencyUsers(filters?: UserSearchFilters): Promise<UserListItem[]> {
        try {
            let endpoint = '/users/agency';
            if (filters) {
                const params = new URLSearchParams();
                if (filters.page)
                    params.append('page', filters.page.toString());
                if (filters.limit)
                    params.append('limit', filters.limit.toString());
                if (filters.search)
                    params.append('search', filters.search);
                if (filters.role)
                    params.append('role', filters.role);
                if (filters.status)
                    params.append('status', filters.status);
                if (filters.agency_id)
                    params.append('agency_id', filters.agency_id.toString());
                if (filters.created_after)
                    params.append('created_after', filters.created_after);
                if (filters.created_before)
                    params.append('created_before', filters.created_before);
                const queryString = params.toString();
                if (queryString) {
                    endpoint += `?${queryString}`;
                }
            }
            const response = await this.executeRequest<ApiResponse<UserListItem[]>>(endpoint, {
                method: 'GET',
            }, true, 30000); // Cache for 30 seconds
            return response.data || [];
        }
        catch (error) {
            logger.error('Error fetching agency users:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError('Failed to fetch agency users', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Get user details by ID
     * @param userId - The user ID to fetch
     * @returns Promise resolving to user details
     */
    async getUserById(userId: number): Promise<UserListItem> {
        try {
            if (!userId || userId <= 0) {
                throw new Error('Valid user ID is required');
            }
            const response = await this.executeRequest<ApiResponse<UserListItem>>(`/users/${userId}`, {
                method: 'GET',
            }, true, 60000); // Cache for 1 minute
            return response.data || {} as UserListItem;
        }
        catch (error) {
            logger.error('Error fetching user details:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && error.message.includes('Valid user ID')) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to fetch user details', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Update user status (activate/deactivate)
     * @param userId - The user ID to update
     * @param statusData - Status update data including reason
     * @returns Promise resolving to status update response
     */
    async updateUserStatus(userId: number, statusData: UserStatusUpdateRequest): Promise<UserStatusUpdateResponse> {
        try {
            if (!userId || userId <= 0) {
                throw new Error('Valid user ID is required');
            }
            if (!statusData.status) {
                throw new Error('Status is required');
            }
            const response = await this.executeRequest<UserStatusUpdateResponse>(`/users/${userId}/status`, {
                method: 'PATCH',
                body: JSON.stringify(statusData),
            }, false); // Don't cache PATCH requests
            // Clear relevant caches after successful update
            this.clearCacheForPattern(`/users/${userId}`);
            this.clearCacheForPattern('/users/agency');
            this.clearCacheForPattern('/users/management/stats');
            return response;
        }
        catch (error) {
            logger.error('Error updating user status:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && (error.message.includes('Valid user ID') || error.message.includes('Status is required'))) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to update user status', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Resend password setup email
     * @param userId - The user ID to resend password setup for
     * @returns Promise resolving to resend response
     */
    async resendPasswordSetupEmail(userId: number): Promise<{
        success: boolean;
        message: string;
    }> {
        try {
            if (!userId || userId <= 0) {
                throw new Error('Valid user ID is required');
            }
            const response = await this.executeRequest<{
                success: boolean;
                message: string;
            }>(`/users/${userId}/resend-password-setup`, {
                method: 'POST',
            }, false); // Don't cache POST requests
            return response;
        }
        catch (error) {
            logger.security('Error resending password setup email:', { "arg0": error });
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && error.message.includes('Valid user ID')) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to resend password setup email', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Delete user account
     * @param userId - The user ID to delete
     * @returns Promise resolving to deletion response
     */
    async deleteUser(userId: number): Promise<{
        success: boolean;
        message: string;
    }> {
        try {
            if (!userId || userId <= 0) {
                throw new Error('Valid user ID is required');
            }
            const response = await this.executeRequest<{
                success: boolean;
                message: string;
            }>(`/users/${userId}`, {
                method: 'DELETE',
            }, false); // Don't cache DELETE requests
            // Clear relevant caches after successful deletion
            this.clearCacheForPattern(`/users/${userId}`);
            this.clearCacheForPattern('/users/agency');
            this.clearCacheForPattern('/users/management/stats');
            this.clearCacheForPattern('/users/recent');
            return response;
        }
        catch (error) {
            logger.error('Error deleting user:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && error.message.includes('Valid user ID')) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to delete user account', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Get recent user creations
     * @param limit - Maximum number of recent users to fetch (default: 10)
     * @returns Promise resolving to list of recent user creations
     */
    async getRecentUserCreations(limit: number = 10): Promise<UserListItem[]> {
        try {
            if (limit <= 0 || limit > 100) {
                throw new Error('Limit must be between 1 and 100');
            }
            const response = await this.executeRequest<ApiResponse<UserListItem[]>>(`/users/recent?limit=${limit}`, {
                method: 'GET',
            }, true, 30000); // Cache for 30 seconds
            return response.data || [];
        }
        catch (error) {
            logger.error('Error fetching recent user creations:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && error.message.includes('Limit must be')) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to fetch recent user creations', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Search users by name or email
     * @param query - Search query string
     * @param filters - Optional additional search filters
     * @returns Promise resolving to list of matching users
     */
    async searchUsers(query: string, filters?: Omit<UserSearchFilters, 'search'>): Promise<UserListItem[]> {
        try {
            if (!query || query.trim().length < 2) {
                throw new Error('Search query must be at least 2 characters long');
            }
            const params = new URLSearchParams();
            params.append('q', query.trim());
            if (filters) {
                if (filters.page)
                    params.append('page', filters.page.toString());
                if (filters.limit)
                    params.append('limit', filters.limit.toString());
                if (filters.role)
                    params.append('role', filters.role);
                if (filters.status)
                    params.append('status', filters.status);
                if (filters.agency_id)
                    params.append('agency_id', filters.agency_id.toString());
                if (filters.created_after)
                    params.append('created_after', filters.created_after);
                if (filters.created_before)
                    params.append('created_before', filters.created_before);
            }
            const response = await this.executeRequest<ApiResponse<UserListItem[]>>(`/users/search?${params.toString()}`, {
                method: 'GET',
            }, true, 15000); // Cache for 15 seconds (shorter for search results)
            return response.data || [];
        }
        catch (error) {
            logger.error('Error searching users:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && error.message.includes('Search query must be')) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to search users', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Bulk create users (for coordinators who need to create multiple accounts)
     * @param users - Array of user creation data
     * @returns Promise resolving to bulk creation response
     */
    async bulkCreateUsers(users: CreateUserRequest[]): Promise<BulkCreateUsersResponse> {
        try {
            if (!users || users.length === 0) {
                throw new Error('Users array is required and cannot be empty');
            }
            if (users.length > 100) {
                throw new Error('Cannot create more than 100 users at once');
            }
            // Validate each user in the array
            for (let i = 0; i < users.length; i++) {
                const user = users[i];
                if (!user.email || !user.first_name || !user.last_name) {
                    throw new Error(`User at index ${i} is missing required fields (email, first_name, last_name)`);
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(user.email)) {
                    throw new Error(`User at index ${i} has invalid email format`);
                }
            }
            const response = await this.executeRequest<BulkCreateUsersResponse>('/users/bulk-create', {
                method: 'POST',
                body: JSON.stringify({ users }),
            }, false); // Don't cache POST requests
            // Clear relevant caches after successful bulk creation
            this.clearCacheForPattern('/users/management/stats');
            this.clearCacheForPattern('/users/agency');
            this.clearCacheForPattern('/users/recent');
            return response;
        }
        catch (error) {
            logger.error('Error bulk creating users:', error, {});
            if (error instanceof ApiError) {
                throw error;
            }
            if (error instanceof Error && (error.message.includes('Users array is required') ||
                error.message.includes('Cannot create more than') ||
                error.message.includes('missing required fields') ||
                error.message.includes('invalid email format'))) {
                throw new ApiError(error.message, 400, 'VALIDATION_ERROR');
            }
            throw new ApiError('Failed to bulk create users', 500, 'SERVER_ERROR');
        }
    }
    /**
     * Clear all cached responses
     */
    public clearCache(): void {
        this.requestCache.clear();
        this.pendingRequests.clear();
        // User management cache cleared
    }
    /**
     * Clear cache for specific endpoint pattern
     */
    public clearCacheForPattern(pattern: string): void {
        const keysToDelete: string[] = [];
        for (const key of this.requestCache.keys()) {
            if (key.includes(pattern)) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => {
            this.requestCache.delete(key);
        });
        if (keysToDelete.length > 0) {
            // Cleared user management cache entries
        }
    }
    /**
     * Get cache statistics for debugging
     * @returns Cache statistics object
     */
    public getCacheStats(): {
        size: number;
        maxSize: number;
        entries: string[];
    } {
        return {
            size: this.requestCache.size,
            maxSize: this.maxCacheSize,
            entries: Array.from(this.requestCache.keys())
        };
    }
    /**
     * Get authentication status for React context integration
     * @returns Object containing authentication state
     */
    public getAuthState(): {
        isAuthenticated: boolean;
        token: string | null;
    } {
        const rawToken = getAuthToken();
        const token =
            typeof rawToken === 'string' && rawToken.length > 0 ? rawToken : null;
        return {
            isAuthenticated: !!token,
            token,
        };
    }
    /**
     * Handle API errors consistently
     * @param error - The error to handle
     * @param fallbackMessage - Fallback message if error doesn't have a message
     * @returns User-friendly error message
     */
    public handleError(error: unknown, fallbackMessage: string = 'An unexpected error occurred'): string {
        return handleApiError(error, fallbackMessage);
    }
    /**
     * Check if an error is a network error
     * @param error - The error to check
     * @returns True if the error is a network-related error
     */
    public isNetworkError(error: unknown): boolean {
        return isNetworkError(error);
    }
    /**
     * Check if an error is an authentication error
     * @param error - The error to check
     * @returns True if the error is authentication-related
     */
    public isAuthError(error: unknown): boolean {
        return isAuthError(error);
    }
}
// Export singleton instance
export const userManagementService = new UserManagementService();
export default userManagementService;
